package one.devsbox.www.ui.campaigns.edit

import androidx.compose.runtime.*
import kotlinx.coroutines.CoroutineScope
import kotlinx.datetime.Clock
import kotlinx.datetime.DateTimeUnit
import kotlinx.datetime.LocalDate
import kotlinx.datetime.plus
import one.devsbox.commons.EMPTY
import one.devsbox.commons.nowDate
import one.devsbox.shared.ads.SharedIO
import one.devsbox.www.provider.LocalNavigationCallbackProvider
import one.devsbox.www.provider.NavigationCallbackProvider
import one.devsbox.www.tools.ArgsId
import one.devsbox.www.tools.EFieldRegularDate
import one.devsbox.www.tools.EFieldTextValue
import tk.mallumo.compose.navigation.LocalNavigation
import tk.mallumo.compose.navigation.LocalNavigationArgs
import tk.mallumo.compose.navigation.Navigation
import tk.mallumo.compose.navigation.args
import tk.mallumo.compose.navigation.viewmodel.viewModel

class CampaignECtx private constructor(
    val vm: CampaignEVM,
    val nav: Navigation,
    val args: ArgsId,
    val navCallback: NavigationCallbackProvider
) {
    val action get() = vm.action

    companion object {
        @Composable
        fun get(): CampaignECtx {
            val nav = LocalNavigation.current
            val navCallback = LocalNavigationCallbackProvider.current
            val args = LocalNavigationArgs.current.args<ArgsId>()
            val vm = nav.viewModel(CampaignEVM::class)
            return remember(args.id) {
                CampaignECtx(vm, nav, args, navCallback).apply {
                    vm.setup(this)
                }
            }
        }
    }

    class Editor(
        val scope: CoroutineScope,
        val campaign: SharedIO.CampaignInfo,
        val locations: List<SharedIO.LocationWithDevices>,
        val deviceDetails: Map<Long, SharedIO.DeviceSimple>
    ) {
        val data = SharedIO.CampaignCore(
            name = campaign.name,
            dStart = campaign.dStart,
            dEnd = campaign.dEnd,
            state = campaign.state,
            visual = campaign.visual.map {
                SharedIO.CampaignCore.Entry(
                    id = it.id,
                    duration = it.duration,
                    devices = it.devices.toSet()
                )
            }
        )

        val name = EFieldTextValue(data::name)
        val startDate = EFieldRegularDate(data::dStart)
        val endDate = EFieldRegularDate(data::dEnd)

        var rememberedStartDate by mutableStateOf(if (campaign.dStart == LocalDate.EMPTY) Clock.nowDate() else campaign.dStart)
        var rememberedEndDate by mutableStateOf(
            if (campaign.dEnd == LocalDate.EMPTY) Clock.nowDate().plus(1, DateTimeUnit.MONTH) else campaign.dEnd
        )

        var durationSeconds by mutableStateOf(run {
            val videoVisuals = campaign.visual.filter { one.devsbox.shared.files.ResourceType[it.type] == one.devsbox.shared.files.ResourceType.VIDEO }
            if (videoVisuals.isNotEmpty()) {
                (videoVisuals.maxOf { it.duration } / 1000).toInt()
            } else {
                campaign.visual.firstOrNull { one.devsbox.shared.files.ResourceType[it.type] == one.devsbox.shared.files.ResourceType.IMAGE }?.let {
                    val sec = (it.duration / 1000).toInt()
                    if (sec < 10) 10 else sec
                } ?: 10
            }
        })

        var searchQuery by mutableStateOf("")

        val selectedDeviceIds = mutableStateListOf<Long>().apply {
            campaign.visual.forEach { addAll(it.devices) }
        }

        val selectedMediaByAspectRatio = mutableStateMapOf<String, SharedIO.MediaCore?>().apply {
            campaign.visual.forEach { if (it.id > 0) put(it.aspectRatio, it) }
        }

        fun getSelectedAspectRatios(): List<String> {
            return selectedDeviceIds.mapNotNull { id ->
                deviceDetails[id]?.aspectRatio
            }.distinct()
        }

        fun getDeviceIdsForAspectRatio(ar: String): List<Long> {
            return selectedDeviceIds.filter { id ->
                deviceDetails[id]?.aspectRatio == ar
            }
        }

        fun toggleDevice(deviceId: Long) {
            if (selectedDeviceIds.contains(deviceId)) {
                selectedDeviceIds.remove(deviceId)
            } else {
                selectedDeviceIds.add(deviceId)
            }
        }
    }
}
